import { CUSTOM_JSON_COMPONENT_CONFIG_LOADERS } from '@apps/websiteCustomJsonComponents/config';
import { JsonDataBlobComponentFragment } from '@graphql/generated';
import React, { useEffect, useState, Suspense } from 'react';
import type { CustomJsonComponentConfig } from '@apps/websiteCustomJsonComponents/config';

type GuestJsonComponentWrapperProps = {
  jsonComponentData: JsonDataBlobComponentFragment;
};

export const GuestSiteCustomJsonComponentWrapper = (props: GuestJsonComponentWrapperProps) => {
  const { jsonComponentData } = props;
  const data = jsonComponentData.containerContent;
  const [config, setConfig] = useState<CustomJsonComponentConfig | null>(null);
  const [loadingState, setLoadingState] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;

    const loadConfig = async () => {
      try {
        if (cancelled) return;
        setLoadingState('loading');
        const loader = CUSTOM_JSON_COMPONENT_CONFIG_LOADERS.find(l => l.componentType === data?.type);
        if (loader) {
          const loadedConfig = await loader.loader();

          if (cancelled) return;

          if (loadedConfig.schemaUrl === data?.schemaUrl || loadedConfig.componentType === data?.type) {
            if (cancelled) return;
            setConfig(loadedConfig);
            setLoadingState('loaded');
          } else {
            if (cancelled) return;
            setLoadingState('error');
          }
        } else {
          if (!cancelled) {
            setLoadingState('error');
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load component config:', error);
          setLoadingState('error');
        }
      }
    };

    void loadConfig();

    return () => {
      cancelled = true;
    };
  }, [data?.schemaUrl, data?.type]);

  if (loadingState === 'loading' || !config) {
    return null;
  }

  if (loadingState === 'error') {
    return null;
  }

  const Component = config.guestSiteComponent;

  return (
    <Suspense fallback={null}>
      <Component
        {...props}
        data={data}
      />
    </Suspense>
  );
};
